import React from 'react';
import { motion } from 'framer-motion';
import ShareBlogButton from '../../../components/ShareBlogButton/ShareBlogButton';
import './ai-powered-communication-platform-for-deaf-and-mute.css';
import aiCommImg from '../../../assets/images/AI Powered Communication Platform.avif';

const AiPoweredCommunicationPlatformForDeafAndMute = () => {
  return (
    <motion.div 
      className="project-detail-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <motion.h1 
        className="project-heading"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        AI Powered <span>Communication Platform</span> for Deaf and Mute
      </motion.h1>

      <motion.p 
        className="last-updated"
        initial={{ x: 50, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.4 }}
      >
        <br />
        Published On: 30th May 2025<br />
        🕒 Last Updated On: 30th May 2025
      </motion.p>

      <div className="project-content">
        <motion.div 
          className="project-left"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <img src={aiCommImg} alt="AI Communication Platform" className="project-image" />
          <h2 className="project-subtitle">AI Powered Communication Platform</h2>
          <span className="project-category">AI & ML</span>
          <p className="project-author">Author: Praveen Kumar B</p>
          <p className="project-author">🕒 3 Mins Read</p>
          <div className="blog-action-buttons">
            <a href="https://under-construction-work.netlify.app/" className="visit-project-button">→ Visit the Project</a>
            <ShareBlogButton
              blog={{
                id: "ai-powered-communication-platform-for-deaf-and-mute",
                title: "AI Powered Communication Platform for Deaf and Mute",
                path: "/blogs/blogdetail/ai-powered-communication-platform-for-deaf-and-mute",
                category: "AI & ML"
              }}
            />
          </div>
        </motion.div>

        <motion.div 
          className="project-right"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <h3 className="section-title">Introduction ✨</h3>
          <p className="overview-text">
            Imagine a world where communication barriers no longer exist for the deaf and mute. That's exactly what we aimed to solve with our AI-powered platform — a bridge that empowers seamless interaction for everyone, regardless of hearing or speaking abilities. 🚀
          </p>

          <h3 className="section-title">The Problem We Wanted to Solve</h3>
          <p className="overview-description">
            Millions of people struggle to connect due to hearing or speaking limitations. Traditional methods often fall short. Our mission was to create a smarter, AI-driven solution that transforms how deaf and mute individuals engage with others — simply, naturally, and instantly. 💬🤝
          </p>

          <h3 className="section-title">Designing the Experience 🧠</h3>
          <p>
            We designed a sleek and friendly web and mobile interface with easy login/sign-up forms. Inside, users can interact through intelligent models tailored to their communication needs. The interface feels effortless while being powerful in functionality. 🖥️📱
          </p>

          <h3 className="section-title">Key Features That Made It Work</h3>
          <ul className="feature-list">
            <li><strong>AI-Powered Sign-to-Text:</strong> Users perform a sign (e.g., "Hello") and the system instantly displays the translated text. ✋➡️📝</li>
            <li><strong>Speech-to-Text Conversion:</strong> Microphone input is processed in real-time using <code>WebkitSpeechRecognition</code> and transformed into readable text for the deaf. 🎤➡️📄</li>
            <li><strong>Keyword Extraction:</strong> With Python's NLTK library, the platform extracts and emphasizes the most meaningful keywords. 🔍💡</li>
            <li><strong>Text-to-Sign Animation:</strong> A friendly animated avatar visualizes the inputted text as sign language for a better understanding. 🤖👐</li>
            <li><strong>100+ Trained Signs:</strong> The platform supports over 100 American Sign Language gestures for enhanced accuracy. 🤟📚</li>
            <li><strong>Robust Dataset:</strong> We used a rich image dataset with ASL-labeled hand gestures, optimized using CNNs and Transformers. 🧠📈</li>
            <li><strong>Live Dashboard:</strong> Users can manage conversations and preferences from an interactive dashboard. 🧩📊</li>
          </ul>

          <h3 className="section-title">Why Teams Love It ❤️</h3>
          <ul className="benefits-list">
            <li>Improves accessibility and social inclusion. 🌍🧏</li>
            <li>Real-time responses reduce communication lag. ⏱️💬</li>
            <li>Encourages confidence and independence in users. 🧠💪</li>
            <li>Educational tool for learning sign language basics. 📘👨‍🏫</li>
            <li>Seamless use across both web and mobile platforms. 🔄📱</li>
          </ul>

          <h3 className="section-title">The Workflow in Action</h3>
          <ul className="how-it-works-list">
            <li>Users sign up or log in to their personal dashboard. 🔑📋</li>
            <li>Choose between speaking or signing mode. 🎤/🖐️</li>
            <li>Speech or hand gestures are processed instantly. ⚙️💡</li>
            <li>Results are displayed in real-time — text or animation. 🖥️🎞️</li>
            <li>Conversations can be tracked and shared. 🔁📤</li>
          </ul>

          <h3 className="section-title">What Sets It Apart?</h3>
          <ul className="how-it-works-list">
            <li><strong>AI-first Design:</strong> Fast, accurate, and intelligent response system. 🤖⚡</li>
            <li><strong>Cross-Platform Availability:</strong> Accessible from mobile and desktop. 🌐📲</li>
            <li><strong>Privacy Focus:</strong> Data is encrypted and never shared. 🔐🛡️</li>
            <li><strong>Adaptive Learning:</strong> The system improves with user interaction. 📊📈</li>
            <li><strong>Tech Stack:</strong> Python, JavaScript, Next.js, Firebase for robust performance. 🛠️🔥</li>
          </ul>

          <h3 className="section-title">Final Thoughts 🎯</h3>
          <p>
            Our <strong>AI Powered Communication Platform</strong> is more than just a tool — it’s a revolution in inclusive communication. Whether you’re deaf, mute, or just want to engage better, this platform offers a seamless bridge to a more connected world. Try it out — and start a conversation that truly matters. 💬🌐
          </p>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default AiPoweredCommunicationPlatformForDeafAndMute;
